import { useAppDispatch, useAppSelector } from "@hooks/redux";
import "./index.scss";
import { useVideoPlayer } from "@hooks/videoplayer";
import { VideoPlayerRef } from "@layouts/GlobalVideoModal/type";
import { updateDownloadingVideos } from "@store/globalVideoSlice";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Events } from "xgplayer";

interface VideoPlayerProps {
  videoURL: string;
  videoId: string;
  className?: string;
  applySuccess?: () => void;
  onTimeUpdate?: (time: number) => void;
  onPause?: () => void;
  onPlay?: () => void;
  scriptText?: string;
}

const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(
  ({ videoURL, videoId, className, onTimeUpdate, onPause, onPlay, scriptText }, ref) => {
    const dispatch = useAppDispatch();
    const { region } = useAppSelector((state) => state.globalReducer);

    const { refPlayer } = useVideoPlayer(
      {
        id: "videoplayer",
        url: videoURL,
        ignores: ["cssFullScreen", "playbackrate"],
        download: false,
        width: 268,
        height: 477,
      },
      [
        {
          eventName: Events.TIME_UPDATE,
          handle: () => {
            if (refPlayer.current) {
              onTimeUpdate && onTimeUpdate(refPlayer.current?.currentTime as number);
            }
          },
        },
        {
          eventName: Events.USER_ACTION,
          handle: (data: any) => {
            if (data.eventType === "click" && data.action === "download") {
              dispatch(updateDownloadingVideos({ region: region, videoId: videoId as string }));
            }
          },
        },
        ...(onPause ? [{ eventName: Events.PAUSE, handle: () => onPause() }] : []),
        ...(onPlay ? [{ eventName: Events.PLAY, handle: () => onPlay() }] : []),
      ],
      {
        kalo_pos: "videoplayer",
        kalo_type: videoId as string,
      },
    );
    const setCurrentTime = (currentTime: number) => {
      if (refPlayer.current) {
        if (currentTime > refPlayer.current.duration) {
          refPlayer.current.pause();
        } else {
          refPlayer.current.currentTime = currentTime;
        }
      }
    };
    useImperativeHandle(ref, () => ({
      setCurrentTime: setCurrentTime,
      emitUserAction: (e: any, type: string) => {
        refPlayer.current?.emitUserAction(e, type, {});
      },
    }));

    const [controlsVisible, setControlsVisible] = useState(false);

    useEffect(() => {
      const handleVisibilityChange = () => {
        if (document.hidden) {
          refPlayer.current?.pause();
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      };
    }, []);

    useEffect(() => {
      const el = document.getElementById("videoplayer");
      if (!el) return;

      const checkClass = () => setControlsVisible(!el.classList.contains("xgplayer-inactive"));
      const observer = new MutationObserver(checkClass);
      observer.observe(el, { attributes: true, attributeFilter: ["class"] });
      checkClass();

      return () => observer.disconnect();
    }, [videoURL]);

    return (
      <div className={className}>
        <div id="videoplayer" className="rounded-[8px]"></div>
        {scriptText && (
          <div
            className={`absolute rounded-[8px] left-[8px] right-[8px] text-white p-[12px] max-h-[160px] overflow-y-auto kds-scroll-thin transition-[bottom] duration-200 ease-in-out ${controlsVisible ? "bottom-[50px]" : "bottom-[10px]"}`}
            style={{
              background: "rgba(34, 34, 34, 0.50)",
              backdropFilter: "blur(10px)",
            }}
          >
            {scriptText}
          </div>
        )}
      </div>
    );
  },
);

export default VideoPlayer;
